import { useEffect } from 'react'
import { useMedia } from './useMedia'

/**
 * Faz as secoes aparecerem conforme o visitante desce.
 *
 * Duas decisoes daqui foram pagas com bug em producao, entao ficam explicadas:
 *
 * 1. Varredura no scroll em vez de IntersectionObserver. O observer nao dispara
 *    quando um elemento cruza de "abaixo da tela" para "acima da tela" num
 *    quadro so — rolagem rapida ou pulo por ancora deixavam secoes invisiveis
 *    para sempre. A varredura tambem revela o que ja passou.
 *
 * 2. Sem throttle por requestAnimationFrame. Se um quadro agendado nao chega a
 *    rodar (aba em segundo plano, webview que nao pinta), a trava do throttle
 *    nunca e liberada e nada mais aparece. O custo de rodar direto e baixo: a
 *    lista de pendentes so encolhe.
 */
export function useReveal() {
  const { reduced } = useMedia()

  useEffect(() => {
    // Itens das listas longas entram em cascata, um apos o outro, em vez de o
    // bloco inteiro aparecer de uma vez. Marcados aqui, e nao no JSX, para nao
    // encher cada componente de data-reveal e data-delay repetidos.
    const CASCATAS: Array<[string, number, number]> = [
      ['.ecol ul', 40, 45],
      ['.checks', 30, 40],
    ]
    CASCATAS.forEach(([seletor, base, passo]) => {
      document.querySelectorAll<HTMLElement>(seletor).forEach((lista) => {
        Array.from(lista.children).forEach((item, i) => {
          item.setAttribute('data-reveal', 'up')
          item.setAttribute('data-delay', String(base + i * passo))
        })
      })
    })

    const itens = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'))

    if (reduced) {
      itens.forEach((el) => el.classList.add('is-in'))
      return
    }

    // Os "mask" ganham um container com overflow hidden (efeito cortina). E o
    // container que precisa ser medido: o filho, recortado, nunca "entra" na
    // tela sozinho.
    const observado = new WeakMap<HTMLElement, HTMLElement>()
    itens.forEach((el) => {
      let alvo: HTMLElement = el
      if (el.getAttribute('data-reveal') === 'mask' && !el.parentElement?.classList.contains('mask-wrap')) {
        const wrap = document.createElement('span')
        wrap.className = 'mask-wrap'
        el.parentNode?.insertBefore(wrap, el)
        wrap.appendChild(el)
        alvo = wrap
      } else if (el.parentElement?.classList.contains('mask-wrap')) {
        alvo = el.parentElement
      }
      observado.set(el, alvo)
    })

    let pendentes = itens.slice()

    const revelar = (el: HTMLElement, imediato: boolean) => {
      const atraso = imediato ? 0 : parseInt(el.getAttribute('data-delay') ?? '0', 10)
      el.style.setProperty('--d', `${atraso}ms`)
      el.classList.add('is-in')
    }

    const varrer = () => {
      if (!pendentes.length) return
      const limite = window.innerHeight * 0.86 // dispara um pouco mais tarde: o movimento fica visivel
      const restantes: HTMLElement[] = []
      for (const el of pendentes) {
        const caixa = observado.get(el)!.getBoundingClientRect()
        if (caixa.top < limite && caixa.bottom > 0) revelar(el, false)
        else if (caixa.bottom <= 0) revelar(el, true)
        else restantes.push(el)
      }
      pendentes = restantes
    }

    window.addEventListener('scroll', varrer, { passive: true })
    window.addEventListener('resize', varrer, { passive: true })
    document.addEventListener('visibilitychange', varrer)
    window.addEventListener('pageshow', varrer)
    varrer()

    return () => {
      window.removeEventListener('scroll', varrer)
      window.removeEventListener('resize', varrer)
      document.removeEventListener('visibilitychange', varrer)
      window.removeEventListener('pageshow', varrer)
    }
  }, [reduced])
}
